import { Box, SxProps } from '@mui/material';

import { CasSecondaryButton } from 'src/compass/shared/components/CasButton/CasSecondaryButton';
import CasEditableDialog from 'src/compass/shared/components/CasTable/CasEditableDialog';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import Iconify from 'src/components/iconify';

import CustomerContactsForm from './CustomerContactsForm';

interface Props {
  // Exactly one of these. SCE Reports holds a grower id; the Customers dialog holds the customer's
  // own id. The server resolves either to the same customer (see SceContactController).
  growerId?: any;
  entityId?: number;
  insuredName?: string | null;
  // Fired after the contacts are saved OR deleted. Callers refresh whatever they derived from the
  // old contacts - see both call sites, which each refetch their own recipient sources.
  onChanged: VoidFunction;
  // The compose window's title bar is cramped enough to drop this to its icon below MD; the Batch
  // Send row pane has the room and passes neither.
  sx?: SxProps;
  labelSx?: SxProps;
  // The list toolbar shows the button before an insured has resolved to a grower, so it can be
  // offered and disabled the way New is, rather than appearing and disappearing.
  disabled?: boolean;
  // "Contacts" where it sits among other actions; "Edit Contacts" on the Contacts tab, where a
  // bare "Edit" would collide with the customer dialog's own Edit a few hundred pixels away.
  label?: string;
}

/*
  Edit one customer's SCE contacts, from wherever a message to that customer is being composed.

  Addressed by grower id, which is what both call sites hold; the customer it resolves to is the
  server's business. Two growers that share a customer therefore open the same contacts.

  Shared by the SCE Reports list toolbar, the single-send compose dialog and the Batch Send row
  pane so they cannot drift: same icon, same label, same editor, same entity. It owns the
  open/closed state, because nothing outside it needs to know - CasEditableDialog self-gates on
  that boolean and closes itself after a save.
*/
export default function CustomerContactsButton({
  growerId,
  entityId,
  insuredName,
  onChanged,
  sx,
  labelSx,
  disabled = false,
  label = 'Contacts',
}: Props) {
  const dialog = useCasBoolean();

  // The two routes differ only in how they are addressed, so pick the one matching the id in hand.
  const byEntity = entityId != null;
  const entity = byEntity ? 'SceContact/Customer' : 'SceContact';
  const id = byEntity ? entityId : growerId;

  return (
    <>
      <CasSecondaryButton
        startIcon={<Iconify icon="flowbite:address-book-solid" />}
        size="small"
        onClick={dialog.setTrue}
        aria-label={label}
        title={label}
        disabled={disabled}
        sx={sx}
      >
        <Box component="span" sx={labelSx}>
          {label}
        </Box>
      </CasSecondaryButton>
      {/*
        Mounted only while open. CasEditableDialog calls useRetrieveDetailsById unconditionally -
        the hook runs before its own `if (!editDialog.value) return null` - so left mounted it
        keeps one cached response for the whole page visit. Nothing invalidates that key on save,
        so reopening after an Update showed the PRE-save values and the save looked like it had
        silently failed. Unmounting on close makes the next open a fresh fetch (staleTime 0).
      */}
      {dialog.value && (
        <CasEditableDialog
          maxWidth="lg"
          entityTitle="Customer Contacts"
          entity={entity}
          editForm={CustomerContactsForm}
          dialog={dialog}
          id={id}
          // Deliberately argument-less: CasEditableDialog passes the saved rows on save but nothing at
          // all on delete, so anything reading the first argument would be broken by a deletion.
          onChanged={() => onChanged()}
          editFormAdditionalData={{ insuredName, entity }}
        />
      )}
    </>
  );
}
